import React from 'react';

import { TFunction } from 'i18next';
import {
  BlocksIcon,
  BookMarkedIcon,
  ZapIcon,
  BoxIcon,
  HashIcon,
  LightbulbIcon,
  LucideIcon,
  MailIcon,
  MessageCircleIcon,
  MessageSquareIcon,
  MicIcon,
  PhoneIcon,
  PlayIcon,
  TagIcon,
  ThumbsUpIcon,
  TrendingUpIcon,
  UserRoundIcon,
} from 'lucide-react';

import { ALL_RATINGS } from '@/modules/analytics/utils/expandRatings';
import { InsightFilter, LogsAnalytics } from '@/shared/types/analytics';
import { Brain } from '@/shared/types/brain';
import { Desk } from '@/shared/types/desk';
import { Member } from '@/shared/types/member';

/**
 * Returns the appropriate icon for a filter type
 */
export const getFilterIcon = (filterType: string): LucideIcon | undefined => {
  const iconMap: Record<string, LucideIcon> = {
    channels: BlocksIcon,
    tags: TagIcon,
    environments: BoxIcon,
    deskIds: BoxIcon,
    brainIds: ZapIcon,
    agentIds: UserRoundIcon,
    isCovered: ThumbsUpIcon,
    isTest: PlayIcon,
    isContained: ThumbsUpIcon,
    isMeaningful: ThumbsUpIcon,
    hasCollection: BookMarkedIcon,
    hasRecording: MicIcon,
    hasVoicemail: PhoneIcon,
    minNumUserMessages: MessageCircleIcon,
    ratings: ThumbsUpIcon,
    csatBrain: ThumbsUpIcon,
    goalAchieved: ThumbsUpIcon,
    userSentiment: ThumbsUpIcon,
    sessionId: HashIcon,
    userId: UserRoundIcon,
    userName: UserRoundIcon,
    userEmail: MailIcon,
    channelUserId: UserRoundIcon,
    externalUserId: UserRoundIcon,
    insightFilters: LightbulbIcon,
    maxConfidence: TrendingUpIcon,
    reactions: ThumbsUpIcon,
    firstMessageType: MessageSquareIcon,
  };

  return iconMap[filterType];
};

/**
 * Returns the human-readable title for a filter type
 */
export const getFilterTitle = (
  filterType: string,
  t: TFunction,
  scope?: 'brain' | 'account'
): string => {
  // Use i18n for translatable filter names
  const i18nMap: Record<string, string> = {
    // Using analytics.fields keys
    ratings: scope === 'account' ? t('analytics.fields.csat') : t('analytics.fields.user_csat'),
    csatBrain: t('analytics.fields.ai_csat_score'),
    tags: t('analytics.fields.tags'),
    sessionId: t('analytics.fields.session_id'),
    userId: t('analytics.fields.user_id'),
    userName: t('analytics.fields.username'),
    userEmail: t('analytics.fields.user_email'),
    channelUserId: t('analytics.fields.channel_user_id'),
    externalUserId: t('analytics.fields.external_user_id'),
    goalAchieved: t('analytics.fields.ai_agent_goal'),
    userSentiment: t('analytics.fields.user_sentiment'),
    isTest: t('brain_logs.filters.groups.preview_conversations'),
    agentIds: t('analytics.human_agents'),

    // Using other common keys
    channels: t('common.channels'),
    environments: t('common.environments'),
    deskIds: t('common.environments'),
    brainIds: t('common.ai-agents'),
    hasCollection: t('brain_logs.filters.groups.collections_recalled'),
    hasRecording: t('brain_logs.filters.groups.voice_recordings'),
    hasVoicemail: t('brain_logs.filters.groups.voicemail'),
    isCovered: t('analytics.coverage'),
    isContained: t('analytics.containment'),
    languageModelIds: t('brain_logs.filters.groups.language_models'),
    minNumUserMessages: t('brain_logs.filters.metrics.user_messages'),
    reactions: t('analytics.fields.reactions'),
    isMeaningful: t('brain_logs.filters.metrics.meaningful'),
    firstMessageType: t('analytics.fields.first_message_type'),
  };

  if (i18nMap[filterType]) {
    return i18nMap[filterType];
  }

  const titleMap: Record<string, string> = {
    // Basic filters - matching useFiltersData exactly
    channels: 'Channels',
    tags: 'Tags',
    environments: 'Environments',
    deskIds: 'Environments',
    brainIds: 'Brains',
    agentIds: 'Human agents',
    languageModelIds: 'Language models',

    // Boolean filters - matching useFiltersData exactly
    isCovered: 'Coverage',
    isTest: 'Test conversations',
    isContained: 'Containment',
    isMeaningful: 'Meaningful',
    hasRecording: 'Voice recordings',
    hasVoicemail: 'Voicemail',

    // Number filters - matching useFiltersData exactly
    minNumUserMessages: 'Number of user messages',
    ratings: 'User CSAT',
    csatBrain: 'AI CSAT',
    goalAchieved: 'AI Agent Goal',
    userSentiment: 'User Sentiment',

    // Text filters - matching useFiltersData exactly
    sessionId: 'Conversation ID',
    userId: 'User ID',
    userName: 'User Name',
    userEmail: 'User Email',
    channelUserId: 'Channel User ID',
    externalUserId: 'External User ID',

    // Complex filters - matching useFiltersData exactly
    insightFilters: 'Insights',
    maxConfidence: 'Max Confidence',
    reactions: 'Reactions',
  };

  return titleMap[filterType] || filterType;
};

const formatTickedStars = (stars: number[], t: TFunction): string => {
  if (stars.length === ALL_RATINGS.length) {
    return t('analytics.any_rating');
  }
  if (stars.length === 1) {
    return t('analytics.star', { count: stars[0] });
  }
  return t('analytics.rating_stars', { 0: stars.join(', ') });
};

/**
 * Formats the filter value for display in the bar
 */
export const formatFilterValue = (
  filter: LogsAnalytics,
  t: TFunction,
  desks?: Desk[],
  brains?: Brain[],
  languageModelNames?: Record<string, string>,
  scope: 'brain' | 'account' = 'brain',
  members?: Member[]
): string => {
  const { type } = filter as unknown as { type: string };

  // Boolean filters
  if (type === 'isCovered') {
    if (filter.isCovered === null || filter.isCovered === undefined) {
      return ''; // Don't display if null/undefined
    }
    return filter.isCovered
      ? t('filters.states.coverage.covered')
      : t('filters.states.coverage.not_covered');
  }

  if (type === 'isTest') {
    if (filter.isTest === null || filter.isTest === undefined) {
      return ''; // Don't display if null/undefined
    }
    return filter.isTest ? t('filters.states.preview.show') : t('filters.states.preview.hide');
  }

  if (type === 'isContained') {
    if (filter.isContained === null || filter.isContained === undefined) {
      return ''; // Don't display if null/undefined
    }
    return filter.isContained
      ? t('filters.states.containment.contained')
      : t('filters.states.containment.not_contained');
  }

  if (type === 'isMeaningful') {
    if (filter.isMeaningful === null || filter.isMeaningful === undefined) {
      return ''; // Don't display if null/undefined
    }
    return filter.isMeaningful
      ? t('filters.states.meaningful.yes')
      : t('filters.states.meaningful.no');
  }

  if (type === 'hasCollection') {
    if (filter.hasCollection === null || filter.hasCollection === undefined) {
      return ''; // Don't display if null/undefined
    }
    return filter.hasCollection
      ? t('filters.states.collections.recalled')
      : t('filters.states.collections.not_recalled');
  }

  if (type === 'hasRecording') {
    if (filter.hasRecording === null || filter.hasRecording === undefined) {
      return '';
    }
    return filter.hasRecording
      ? t('brain_logs.filters.voice_recordings.has_recordings')
      : t('brain_logs.filters.voice_recordings.no_recordings');
  }

  if (type === 'hasVoicemail') {
    if (filter.hasVoicemail === null || filter.hasVoicemail === undefined) {
      return '';
    }
    return filter.hasVoicemail
      ? t('brain_logs.filters.voicemail.detected')
      : t('brain_logs.filters.voicemail.not_detected');
  }

  if (type === 'reactions') {
    if (filter.reactions === null || filter.reactions === undefined) {
      return '';
    }
    return filter.reactions ? t('analytics.with_thumbs_up') : t('analytics.with_thumbs_down');
  }

  if (type === 'firstMessageType') {
    if (filter.firstMessageType !== 'proactive' && filter.firstMessageType !== 'reactive') {
      return '';
    }
    return t(`filters.states.first_message_type.${filter.firstMessageType}`);
  }

  // Array-based filters
  if (type === 'channels' && filter.channels) {
    const channels = filter.channels as string[];
    if (channels.length === 0) {
      return '';
    }
    if (channels.length === 1) {
      return channels[0];
    }
    return `${channels[0]} +${channels.length - 1}`;
  }

  if (type === 'tags' && filter.tags) {
    const tags = filter.tags as string[];
    if (tags.length === 0) {
      return '';
    }
    if (tags.length === 1) {
      return tags[0];
    }
    const mode =
      filter.tagsMode === 'all'
        ? t('analytics.filters.tags_mode_all_short')
        : t('analytics.filters.tags_mode_any_short');
    return `${tags[0]} +${tags.length - 1} (${mode})`;
  }

  if ((type === 'environments' || type === 'deskIds') && filter.deskIds) {
    const deskIds = filter.deskIds as string[];
    if (deskIds.length === 0) {
      return '';
    }

    // Map desk IDs to desk names if desks data is available
    if (desks && desks.length > 0) {
      const selectedDesks = deskIds
        .map((id) => desks.find((desk) => desk.desk_id === id))
        .filter(Boolean);

      if (selectedDesks.length === 0) {
        return 'selected';
      }
      if (selectedDesks.length === 1) {
        return `${selectedDesks[0].name}`;
      }
      return `${selectedDesks[0].name} +${selectedDesks.length - 1}`;
    }

    // Fallback if no desk data available
    if (deskIds.length === 1) {
      return 'selected';
    }
    return `${deskIds.length} selected`;
  }

  if (type === 'brainIds' && (filter as { brainIds?: string[] }).brainIds) {
    const brainIds = (filter as { brainIds?: string[] }).brainIds ?? [];
    if (brainIds.length === 0) {
      return '';
    }

    if (brains && brains.length > 0) {
      const selectedBrains = brainIds
        .map((id) => brains.find((brain) => brain.brain_id === id))
        .filter(Boolean);

      if (selectedBrains.length === 0) {
        return 'selected';
      }
      if (selectedBrains.length === 1) {
        return `${selectedBrains[0].name}`;
      }
      return `${selectedBrains[0].name} +${selectedBrains.length - 1}`;
    }

    if (brainIds.length === 1) {
      return 'selected';
    }
    return `${brainIds.length} selected`;
  }

  if (type === 'agentIds' && filter.agentIds) {
    const agentIds = filter.agentIds as Array<{ value: string; label: string } | string>;
    if (agentIds.length === 0) {
      return '';
    }

    // Names change, ids do not: show the current member name, else the name stored with the filter.
    const nameById = new Map(members?.map((member) => [member.user_id, member.name]));
    const agentLabels = agentIds
      .map((item) => {
        if (typeof item === 'string') {
          return nameById.get(item) ?? item;
        }
        return nameById.get(item.value) ?? item.label;
      })
      .filter(Boolean);

    if (agentLabels.length === 0) {
      return 'selected';
    }
    if (agentLabels.length === 1) {
      return agentLabels[0];
    }
    return `${agentLabels[0]} +${agentLabels.length - 1}`;
  }

  if (type === 'languageModelIds' && filter.languageModelIds) {
    const modelIds = filter.languageModelIds as string[];
    if (modelIds.length === 0) {
      return '';
    }

    // Map model IDs to model names if languageModelNames data is available
    if (languageModelNames && Object.keys(languageModelNames).length > 0) {
      const selectedModels = modelIds.map((id) => languageModelNames[id]).filter(Boolean);

      if (selectedModels.length === 0) {
        return 'selected';
      }
      if (selectedModels.length === 1) {
        return `${selectedModels[0]}`;
      }
      return `${selectedModels[0]} +${selectedModels.length - 1}`;
    }

    // Fallback if no language model names available
    if (modelIds.length === 1) {
      return 'selected';
    }
    return `${modelIds.length} selected`;
  }

  if (type === 'ratings' && filter.ratings) {
    const rawRatings = filter.ratings as (string | number)[];
    if (rawRatings.length === 0) {
      return '';
    }
    // Convert to numbers for processing
    const ratings = rawRatings.map((r) => (typeof r === 'string' ? parseInt(r, 10) : r));

    // Use stored mode if available, otherwise fall back to pattern detection
    const ratingsMode = filter.ratingsMode;
    if (ratingsMode === 'in') {
      return formatTickedStars(ratings, t);
    } else if (ratingsMode === 'moreThan') {
      const minRating = scope === 'account' ? ratings[0] : Math.min(...ratings) - 1;
      return `${t('analytics.more_than_star', { count: minRating })}`;
    } else if (ratingsMode === 'lessThan') {
      const maxRating = ratings.length > 0 ? ratings[0] : 0;
      return `${t('analytics.less_than_star', { count: maxRating })}`;
    } else if (ratingsMode === 'exact') {
      if (ratings.length === 1) {
        return `${t('analytics.star', { count: ratings[0] })}`;
      }
      return `${t('analytics.rating', { count: ratings.length })}`;
    }

    // Fallback to pattern detection for backward compatibility
    const sortedRatings = [...ratings].sort((a, b) => a - b);
    const isConsecutive = sortedRatings.every(
      (val, i) => i === 0 || val === sortedRatings[i - 1] + 1
    );

    if (isConsecutive && sortedRatings[sortedRatings.length - 1] === 5) {
      // "More than X" pattern
      const minRating = sortedRatings[0] - 1;
      return `${t('analytics.more_than_star', { count: minRating })}`;
    }

    if (ratings.length === 1) {
      return `${t('analytics.star', { count: ratings[0] })}`;
    }
    return `${t('analytics.rating', { count: ratings.length })}`;
  }

  if (type === 'csatBrain' && filter.csatBrain) {
    const rawScores = filter.csatBrain as (string | number)[];
    if (rawScores.length === 0) {
      return '';
    }
    // Convert to numbers for processing
    const scores = rawScores.map((s) => (typeof s === 'string' ? parseInt(s, 10) : s));

    // Use stored mode if available, otherwise fall back to pattern detection
    const csatBrainMode = filter.csatBrainMode;
    if (csatBrainMode === 'in') {
      return formatTickedStars(scores, t);
    } else if (csatBrainMode === 'moreThan') {
      // Calculate min from the filter values (they are X+1 to 5)
      const minScore = scores.length > 0 ? Math.min(...scores) - 1 : 0;
      return `${t('analytics.more_than_star', { count: minScore })}`;
    } else if (csatBrainMode === 'exact') {
      if (scores.length === 1) {
        return `${t('analytics.star', { count: scores[0] })}`;
      }
      return `${t('analytics.score', { count: scores.length })}`;
    }

    // Fallback to pattern detection for backward compatibility
    const sortedScores = [...scores].sort((a, b) => a - b);
    const isConsecutive = sortedScores.every(
      (val, i) => i === 0 || val === sortedScores[i - 1] + 1
    );

    if (isConsecutive && sortedScores[sortedScores.length - 1] === 5) {
      // "More than X" pattern
      const minScore = sortedScores[0] - 1;
      return `${t('analytics.more_than_star', { count: minScore })}`;
    }

    if (scores.length === 1) {
      return `${t('analytics.star', { count: scores[0] })}`;
    }
    return `${t('analytics.score', { count: scores.length })}`;
  }

  if (type === 'goalAchieved' && filter.goalAchieved) {
    const values = filter.goalAchieved as number[];
    if (values.length === 0) {
      return '';
    }

    const goalMap: Record<number, string> = {
      1: t('analytics.filters.goalAchieved.achieved'),
      2: t('analytics.filters.goalAchieved.partiallyAchieved'),
      3: t('analytics.filters.goalAchieved.failed'),
    };

    if (values.length === 1) {
      return `${goalMap[values[0]] || values[0]}`;
    }
    // Sort values to ensure consistent display order and show first + count pattern
    const sortedValues = [...values].sort((a, b) => a - b);
    const firstLabel = goalMap[sortedValues[0]] || sortedValues[0];
    return `${firstLabel} +${values.length - 1}`;
  }

  if (type === 'userSentiment' && filter.userSentiment) {
    const values = filter.userSentiment as number[];
    if (values.length === 0) {
      return '';
    }
    const sentimentMap: Record<number, string> = {
      1: t('analytics.positive'),
      2: t('analytics.neutral'),
      3: t('analytics.negative'),
    };
    if (values.length === 1) {
      return `${sentimentMap[values[0]] || values[0]}`;
    }
    // Sort values to ensure consistent display order and show first + count pattern
    const sortedValues = [...values].sort((a, b) => a - b);
    const firstLabel = sentimentMap[sortedValues[0]] || sortedValues[0];
    return `${firstLabel} +${values.length - 1}`;
  }

  // Number filters with operators
  if (type === 'minNumUserMessages') {
    const operator = filter.numUserMessagesOperator || '≥';
    const value = filter.numUserMessagesValue || filter.minNumUserMessages;
    const opText = operator === '≥' ? 'at least' : operator;
    return `${opText} ${value}`;
  }

  // Text filters
  if (type === 'sessionId' && filter.sessionId) {
    return `${filter.sessionId}`;
  }

  if (type === 'userId' && filter.userId) {
    return `${filter.userId}`;
  }

  if (type === 'userName' && filter.userName) {
    return `${filter.userName}`;
  }

  if (type === 'userEmail' && filter.userEmail) {
    return `${filter.userEmail}`;
  }

  if (type === 'channelUserId' && filter.channelUserId) {
    return `${filter.channelUserId}`;
  }

  if (type === 'externalUserId' && filter.externalUserId) {
    return `${filter.externalUserId}`;
  }

  // Insight filters (complex)
  if (type === 'insightFilters' && filter.insightFilters) {
    const insights = filter.insightFilters;
    if (insights.length === 0) {
      return '';
    }
    // Return count since individual insights will be shown as separate items
    return `${insights.length} insights`;
  }

  // Default fallback
  return 'applied';
};

/**
 * Formats insight filter for display as separate items
 */
export const formatInsightFilter = (insight: InsightFilter, t: TFunction): string => {
  const { operator, values, category } = insight;

  // Special case for exists operator - just return the operator name
  if (operator === 'exists') {
    return t('filters.operators.exists', 'exists');
  }

  // Use existing translation keys from filters.operators
  const operatorKey = `filters.operators.${category}.${operator}`;
  let formattedOperator = t(operatorKey);

  // If translation fails, provide user-friendly fallbacks instead of raw operator codes
  if (formattedOperator === operatorKey || formattedOperator === operator) {
    const operatorFallbacks: Record<string, string> = {
      eq: 'Is',
      ne: 'Is not',
      like: 'Contains',
      nlike: 'Does not contain',
      in: 'Is one of',
      nin: 'Is not one of',
      gt: 'More than',
      lt: 'Less than',
      gte: 'More than or equal',
      lte: 'Less than or equal',
      between: 'Between',
      exists: 'exists',
    };
    formattedOperator = operatorFallbacks[operator] || operator;
  }

  // Handle categorical insights (in/nin operators) with multiple values
  if ((operator === 'in' || operator === 'nin') && Array.isArray(values)) {
    if (values.length === 0) {
      return `${formattedOperator} ${t('common.none')}`;
    }
    if (values.length === 1) {
      return `${values[0]}`;
    }
    // Show first value plus count for multiple selections
    return `${values[0]} +${values.length - 1}`;
  }

  const value = Array.isArray(values) ? values[0] : values;

  // For like/nlike operators, show the value in quotes
  if (operator === 'like' || operator === 'nlike') {
    return `${formattedOperator} "${value}"`;
  }

  // For other operators
  return `${formattedOperator} ${value}`;
};

/**
 * Component to render filter icons consistently across the app
 */
export const FilterIconComponent: React.FC<{ type: string; className?: string }> = ({
  type,
  className = 'h-4 w-4 text-muted-foreground',
}) => {
  const Icon = getFilterIcon(type);
  if (!Icon) {
    return null;
  }
  return React.createElement(Icon, { className });
};

/**
 * Empty icon slot matching FilterIcon dimensions, so a filter menu item with no
 * icon stays aligned with its icon'd siblings.
 */
export const FilterIconSpacer: React.FC = () =>
  React.createElement('span', { className: 'h-4 w-4' });
